import { useState, useMemo } from 'react';
import { X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useMailStore } from '../../store/mail';
import { useCreateAccount, useTestAccount } from '../../hooks/useAccounts';
import { usePortal } from '../../store/portal';

interface ProviderPreset {
  label: string;
  imap_host: string;
  imap_port: number;
  smtp_host: string;
  smtp_port: number;
  secure: boolean;
}

const PRESETS: Record<string, ProviderPreset> = {
  gmail: {
    label: 'Gmail',
    imap_host: 'imap.gmail.com',
    imap_port: 993,
    smtp_host: 'smtp.gmail.com',
    smtp_port: 465,
    secure: true,
  },
  outlook: {
    label: 'Outlook / Microsoft 365',
    imap_host: 'outlook.office365.com',
    imap_port: 993,
    smtp_host: 'smtp.office365.com',
    smtp_port: 587,
    secure: false,
  },
  yahoo: {
    label: 'Yahoo Mail',
    imap_host: 'imap.mail.yahoo.com',
    imap_port: 993,
    smtp_host: 'smtp.mail.yahoo.com',
    smtp_port: 465,
    secure: true,
  },
  // "Il mio server di posta": l'utente collega manualmente il proprio server IMAP/SMTP.
  // Nessun preset legato a clienti specifici: la connessione è configurabile dall'utente.
  custom: {
    label: 'Il mio server di posta',
    imap_host: '',
    imap_port: 993,
    smtp_host: '',
    smtp_port: 587,
    secure: false,
  },
};

export function AddAccountDialog() {
  const open = useMailStore((s) => s.addAccountOpen);
  const setOpen = useMailStore((s) => s.setAddAccountOpen);
  const create = useCreateAccount();
  const test = useTestAccount();

  // Server di posta suggerito dal portale (config): se presente, primo preset prefillato.
  const suggestedMailServer = usePortal((s) => s.portal.suggestedMailServer);
  const presets = useMemo<Record<string, ProviderPreset>>(() => {
    const host = (suggestedMailServer ?? '').trim();
    if (!host) return PRESETS;
    return {
      portal: {
        label: 'Il mio portale',
        imap_host: host,
        imap_port: 993,
        smtp_host: host,
        smtp_port: 587,
        secure: false, // 587 STARTTLS
      },
      ...PRESETS,
    };
  }, [suggestedMailServer]);

  const [presetKey, setPresetKey] = useState<string>('gmail');
  const [form, setForm] = useState({
    display_name: '',
    email: '',
    imap_username: '',
    imap_password: '',
  });
  const [custom, setCustom] = useState({
    imap_host: '',
    imap_port: '993',
    smtp_host: '',
    smtp_port: '587',
  });
  const [testResult, setTestResult] = useState<null | { ok: boolean; error?: string }>(null);

  if (!open) return null;
  const preset = presets[presetKey] ?? PRESETS.custom;

  function handlePreset(key: string) {
    setPresetKey(key);
    setTestResult(null);
  }

  async function handleTest() {
    setTestResult(null);
    const payload = buildPayload();
    try {
      const res = await test.mutateAsync(payload);
      setTestResult({ ok: res?.ok ?? false, error: res?.error });
    } catch (err) {
      setTestResult({ ok: false, error: (err as Error).message });
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = buildPayload();
    try {
      await create.mutateAsync(payload);
      setOpen(false);
      setForm({ display_name: '', email: '', imap_username: '', imap_password: '' });
      setTestResult(null);
    } catch (err) {
      setTestResult({ ok: false, error: (err as Error).message });
    }
  }

  function buildPayload() {
    // Provider custom: host/porte dai campi visibili; SMTP secure implicito (465 = TLS, altrimenti STARTTLS)
    const isCustom = presetKey === 'custom';
    const imapHost = isCustom ? custom.imap_host.trim() : preset.imap_host;
    const imapPort = isCustom ? Number(custom.imap_port) || 993 : preset.imap_port;
    const smtpHost = isCustom ? custom.smtp_host.trim() : preset.smtp_host;
    const smtpPort = isCustom ? Number(custom.smtp_port) || 587 : preset.smtp_port;
    const smtpSecure = isCustom ? smtpPort === 465 : preset.secure;
    return {
      display_name: form.display_name || form.email,
      email: form.email,
      imap_host: imapHost,
      imap_port: imapPort,
      imap_secure: true,
      imap_username: form.imap_username || form.email,
      imap_password: form.imap_password,
      imap_auth_method: 'PLAIN' as const,
      smtp_host: smtpHost,
      smtp_port: smtpPort,
      smtp_secure: smtpSecure,
      smtp_username: form.imap_username || form.email,
      smtp_password: form.imap_password,
      default_for_send: false,
    };
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/80 backdrop-blur-sm">
      <div className="w-full max-w-md animate-fade-in rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <h2 className="text-sm font-semibold">Aggiungi account email</h2>
          <button
            onClick={() => setOpen(false)}
            className="rounded p-1 text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-100"
          >
            <X size={14} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          {/* Provider preset */}
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-zinc-500">
              Provider
            </label>
            <div className="grid grid-cols-3 gap-1.5">
              {Object.entries(presets).map(([k, p]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => handlePreset(k)}
                  className={`rounded-md border px-2 py-1.5 text-[11px] font-medium transition ${
                    presetKey === k
                      ? 'border-accent-500/60 bg-accent-500/10 text-accent-300'
                      : 'border-zinc-800 bg-zinc-950/60 text-zinc-400 hover:border-zinc-700'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[10px] text-zinc-500">
              Il tuo provider non è in elenco? Scegli{' '}
              <span className="text-accent-300">Il mio server di posta</span> e inserisci host e
              porte del tuo server IMAP/SMTP.
            </p>
          </div>

          <Input
            label="Nome visualizzato"
            value={form.display_name}
            onChange={(v) => setForm((f) => ({ ...f, display_name: v }))}
            placeholder="Es. Lavoro Netlab"
          />
          <Input
            label="Email"
            type="email"
            required
            value={form.email}
            onChange={(v) => setForm((f) => ({ ...f, email: v }))}
            placeholder="nome@dominio.com"
          />
          <Input
            label="Username IMAP (se diverso dall'email)"
            value={form.imap_username}
            onChange={(v) => setForm((f) => ({ ...f, imap_username: v }))}
            placeholder="auto = email"
          />
          <Input
            label="Password"
            type="password"
            required
            value={form.imap_password}
            onChange={(v) => setForm((f) => ({ ...f, imap_password: v }))}
          />

          {presetKey === 'custom' && (
            <div className="space-y-3 rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Input
                    label="Host IMAP"
                    required
                    value={custom.imap_host}
                    onChange={(v) => setCustom((c) => ({ ...c, imap_host: v }))}
                    placeholder="imap.dominio.com"
                  />
                </div>
                <Input
                  label="Porta IMAP"
                  value={custom.imap_port}
                  onChange={(v) => setCustom((c) => ({ ...c, imap_port: v }))}
                  placeholder="993"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Input
                    label="Host SMTP"
                    required
                    value={custom.smtp_host}
                    onChange={(v) => setCustom((c) => ({ ...c, smtp_host: v }))}
                    placeholder="smtp.dominio.com"
                  />
                </div>
                <Input
                  label="Porta SMTP"
                  value={custom.smtp_port}
                  onChange={(v) => setCustom((c) => ({ ...c, smtp_port: v }))}
                  placeholder="587"
                />
              </div>
              <p className="text-[10px] text-zinc-500">
                IMAP sempre TLS (993). SMTP: porta 465 = TLS, altre porte = STARTTLS.
              </p>
            </div>
          )}

          {testResult && (
            <div
              className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${
                testResult.ok
                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                  : 'border-rose-500/30 bg-rose-500/10 text-rose-300'
              }`}
            >
              {testResult.ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
              {testResult.ok
                ? 'Connessione IMAP + SMTP OK'
                : `Errore: ${testResult.error ?? 'sconosciuto'}`}
            </div>
          )}

          <div className="flex items-center justify-between border-t border-zinc-800 pt-4">
            <button
              type="button"
              onClick={handleTest}
              disabled={test.isPending || !form.email || !form.imap_password}
              className="rounded-md border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 transition hover:bg-zinc-800 disabled:opacity-40"
            >
              {test.isPending ? <Loader2 size={12} className="animate-spin" /> : 'Test connessione'}
            </button>
            <button
              type="submit"
              disabled={create.isPending}
              className="flex items-center gap-2 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-accent-600 disabled:opacity-50"
            >
              {create.isPending && <Loader2 size={12} className="animate-spin" />}
              Aggiungi account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = 'text',
  required,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-zinc-500">
        {label}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-zinc-800 bg-zinc-950/60 px-2.5 py-1.5 text-sm outline-none transition focus:border-accent-500/50"
      />
    </div>
  );
}
